import { Check, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { OrgScope } from '../../constants';
import styles from './OrgSwitcher.module.scss';

const ORGS = [
  { id: OrgScope.Perry, label: 'Perry' },
  { id: OrgScope.Mindspan, label: 'Mindspan' },
];

const OrgSwitcher = ({ value, defaultOrgScope, onSelect, onSetDefault, isSettingDefault }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const activeOrg = ORGS.find((o) => o.id === value);

  if (!activeOrg) return null;

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.triggerLabel}>Org</span>
        <span className={styles.triggerValue}>{activeOrg.label}</span>
        <ChevronDown size={14} strokeWidth={2} className={styles.chevron} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox">
          {ORGS.map((org) => {
            const isActive = org.id === value;
            const isDefault = org.id === defaultOrgScope;
            return (
              <div key={org.id} className={styles.optionRow}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                  onClick={() => {
                    onSelect(org.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    size={12}
                    strokeWidth={2.5}
                    className={styles.optionCheck}
                    style={{ opacity: isActive ? 1 : 0 }}
                    aria-hidden="true"
                  />
                  <span className={styles.optionLabel}>{org.label}</span>
                </button>

                {isDefault ? (
                  <span className={styles.defaultBadge}>Default</span>
                ) : (
                  <button
                    type="button"
                    className={styles.makeDefaultBtn}
                    disabled={isSettingDefault}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetDefault(org.id);
                    }}
                  >
                    {isSettingDefault ? '...' : 'Make default'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

OrgSwitcher.propTypes = {
  value: PropTypes.string,
  defaultOrgScope: PropTypes.string,
  onSelect: PropTypes.func,
  onSetDefault: PropTypes.func,
  isSettingDefault: PropTypes.bool,
};

export default OrgSwitcher;
